import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as Close } from '@/assets/images/common/close.svg';
import TextComponent from '@/components/TextComponent';
import theme from '@/styles/theme';

export interface ModalProps {
  children?: React.ReactNode;
  clickOverlayToClose?: boolean;
  header?: string;
  onClickClose?: () => void;
  thin?: boolean;
}

const useModal = (
  visible = false
): [React.FC<ModalProps>, (value?: boolean) => void, boolean] => {
  const [isVisible, setIsVisible] = useState<boolean>(visible);

  const switchModal = useCallback(
    (value = !isVisible) => {
      setIsVisible(value);
    },
    [isVisible, setIsVisible]
  );

  const ModalComponent = useCallback(
    (props: ModalProps) => {
      const {
        header,
        clickOverlayToClose = true,
        onClickClose,
        children,
        thin
      } = props;

      const onClickOverlay = () => {
        if (clickOverlayToClose) {
          switchModal();
          onClickClose && onClickClose();
        }
      };

      const onClickSwitchModal = () => {
        switchModal();
        onClickClose && onClickClose();
      };

      return (
        <ModalOverlay
          visible={isVisible}
          ref={ref => ref?.focus()}
          id="keyboard"
          tabIndex={0}
          onClick={onClickOverlay}>
          <ModalWrapper thin={thin}>
            <Header visible={!!header}>
              <HeaderTitle>{header}</HeaderTitle>
              <Close
                style={{
                  cursor: 'pointer',
                  width: 16,
                  height: 16
                }}
                onClick={onClickSwitchModal}
              />
            </Header>
            <Body>{children}</Body>
          </ModalWrapper>
        </ModalOverlay>
      );
    },
    [isVisible]
  );

  return [ModalComponent, switchModal, isVisible];
};

export default useModal;

const ModalOverlay = styled.div<{ visible: boolean }>`
  box-sizing: border-box;
  display: ${props => (props.visible ? 'flex' : 'none')};
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 999;
`;

const ModalWrapper = styled.div<{ thin?: boolean }>`
  background-color: ${theme.DF_COLOR.WHITE};
  border-radius: 5px;
  ${({ thin }) => (thin ? '' : 'min-width: 28rem')};
  padding: ${({ thin }) => (thin ? '0px' : '32px')};
`;

const Header = styled.div<{ visible: boolean }>`
  display: ${props => (props.visible ? 'flex' : 'none')};
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0;
`;

const HeaderTitle = styled(TextComponent)`
  font-size: 25px;
  font-weight: 600;
`;

const Body = styled.div``;
